// Länderprofile für Adressen — die eine Stelle, an der steht, was ein Land für
// die Adresserfassung bedeutet: wie seine Postleitzahl aussieht, wie ein
// Beispiel dafür lautet, und in welcher Region Google Adressen vorschlagen soll.
//
// Der Anlass: Bis 08/2026 verlangte die Kontakt-Anlage fünf Ziffern zwischen
// 01067 und 99998 — und zwar als harte Sperre am Speichern-Knopf. Ein
// belgischer Kunde ließ sich damit nicht anlegen, sobald man die Postleitzahl
// eintrug.
//
// Drei Härtegrade, absichtlich abgestuft:
//
//   FEHLER   Deutschland. Die Regel ist bewiesen, der Tippschutz für den
//            Normalfall bleibt vollständig erhalten.
//   HINWEIS  Die fünf Nachbarländer. Das Muster stimmt nach allem, was
//            bekannt ist — aber eine angelesene Regel darf niemanden
//            aussperren. Sie hilft, sie blockiert nicht.
//   STILL    Alles andere, und jede Adresse ohne Landangabe. Im Bestand ist
//            `country` überwiegend leer; „kein Land" darf niemals
//            „ungültig" heißen.
//
// Gespeichert wird weiterhin der deutsche Ländername als Freitext, wie im
// ganzen Bestand. Der ISO-Code wird zur Laufzeit abgeleitet und NIE
// persistiert: Eine eigene Spalte bräuchte einen Backfill, und der wäre
// dieselbe Vermutung wie diese Ableitung — nur eingefroren. Heute heißt ein
// leeres Land „unbekannt, vermutlich Deutschland"; nach einem Backfill hieße
// es „geprüft Deutschland", und das wäre eine Behauptung.

import { NATIONALITIES, DEFAULT_COUNTRY } from "../../../data/nationalities";

export type PlzHaerte = "fehler" | "hinweis" | "still";

export interface Laenderprofil {
  /** ISO 3166-1 alpha-2. */
  code: string;
  /** Muster der Postleitzahl. */
  plz: RegExp;
  /** Beispiel-PLZ — steht als Platzhalter im Feld. */
  beispiel: string;
  /** Klartext für den Hinweis unter dem Feld. */
  form: string;
  /** Nur Ziffern? Steuert die Tastatur auf Mobilgeräten. */
  nurZiffern: boolean;
}

/**
 * Die Länder, für die eine Regel hinterlegt ist. Bewusst kurz gehalten: Der
 * Nationalitäten-Katalog führt 197 Länder, aber eine PLZ-Regel je Land zu
 * pflegen wäre Pflegeaufwand ohne Nutzen. Was hier fehlt, wird nicht geprüft.
 */
export const LAENDERPROFILE: Record<string, Laenderprofil> = {
  DE: { code: "DE", plz: /^\d{5}$/, beispiel: "80331", form: "5 Ziffern", nurZiffern: true },
  AT: { code: "AT", plz: /^\d{4}$/, beispiel: "1010", form: "4 Ziffern", nurZiffern: true },
  BE: { code: "BE", plz: /^\d{4}$/, beispiel: "1000", form: "4 Ziffern", nurZiffern: true },
  CH: { code: "CH", plz: /^\d{4}$/, beispiel: "8001", form: "4 Ziffern", nurZiffern: true },
  // Luxemburg schreibt die PLZ im Inland vierstellig, im internationalen
  // Schriftverkehr oft mit dem Präfix „L-".
  LU: { code: "LU", plz: /^(L-)?\d{4}$/i, beispiel: "L-1150", form: "4 Ziffern, wahlweise mit „L-“", nurZiffern: false },
  // Die Niederlande: vier Ziffern, zwei Buchstaben, üblicherweise mit
  // Leerzeichen dazwischen. Genau daran scheitert jede Prüfung, die nur
  // Ziffern zulässt — und jede Normalisierung, die Buchstaben wegwirft.
  NL: { code: "NL", plz: /^\d{4}\s?[A-Za-z]{2}$/, beispiel: "1012 AB", form: "4 Ziffern und 2 Buchstaben, z. B. 1012 AB", nurZiffern: false },
};

const norm = (s: string | null | undefined) => String(s ?? "").trim().toLowerCase();

/** Deutscher Ländername → ISO-Code. Leerer Name → undefined. */
export function isoCodeZuLand(land: string | null | undefined): string | undefined {
  const l = norm(land);
  if (!l) return undefined;
  // Bestandswerte weichen gelegentlich vom Katalog ab („USA" statt
  // „Vereinigte Staaten") — deshalb zusätzlich der Vergleich auf den Code selbst.
  if (/^[a-z]{2}$/.test(l)) {
    const direkt = NATIONALITIES.find((n) => norm(n.code) === l);
    if (direkt) return direkt.code;
  }
  return NATIONALITIES.find((n) => norm(n.country) === l)?.code;
}

/** Profil zum Ländernamen, oder undefined, wenn keine Regel hinterlegt ist. */
export function profilZuLand(land: string | null | undefined): Laenderprofil | undefined {
  const code = isoCodeZuLand(land);
  return code ? LAENDERPROFILE[code] : undefined;
}

export interface PlzUrteil {
  /** Wie streng gilt das Urteil? */
  haerte: PlzHaerte;
  /** Passt die Eingabe zum Muster? Bei `still` immer true. */
  ok: boolean;
  /** Text unter dem Feld, wenn etwas nicht passt. */
  meldung?: string;
  /** Platzhalter für das Feld. */
  beispiel: string;
  /** Soll das Feld eine Zifferntastatur anbieten? */
  nurZiffern: boolean;
}

/**
 * Bewertet eine Postleitzahl im Licht ihres Landes.
 *
 * Wichtig für die Aufrufer: `ok === false` bedeutet NICHT automatisch
 * „Speichern sperren". Nur bei `haerte === "fehler"` ist das gemeint; bei
 * `"hinweis"` ist der Text eine Hilfestellung und der Knopf bleibt offen.
 */
export function plzPruefen(plz: string | null | undefined, land: string | null | undefined): PlzUrteil {
  const wert = String(plz ?? "").trim();
  const profil = profilZuLand(land);
  const istDeutschland = !land?.trim() || norm(land) === norm(DEFAULT_COUNTRY);

  // Deutschland — oder gar kein Land, was im Bestand faktisch Deutschland
  // heißt. Nur hier wird gesperrt, und beim leeren Land nur nachrangig: eine
  // Adresse ohne Landangabe ist der Normalfall im Altbestand.
  if (istDeutschland) {
    const de = LAENDERPROFILE.DE;
    const passt = !wert || (de.plz.test(wert) && Number(wert) >= 1067 && Number(wert) <= 99998);
    return {
      haerte: land?.trim() ? "fehler" : "hinweis",
      ok: passt,
      meldung: passt ? undefined : "Ungültige PLZ (5-stellig, Deutschland)",
      beispiel: de.beispiel,
      nurZiffern: true,
    };
  }

  // Land gepflegt, aber keine Regel hinterlegt: Wir wissen es nicht, also
  // schweigen wir. Eine französische oder polnische Adresse muss erfassbar sein.
  if (!profil) {
    return { haerte: "still", ok: true, beispiel: "", nurZiffern: false };
  }

  const passt = !wert || profil.plz.test(wert);
  return {
    haerte: "hinweis",
    ok: passt,
    meldung: passt ? undefined : `PLZ in diesem Land: ${profil.form}`,
    beispiel: profil.beispiel,
    nurZiffern: profil.nurZiffern,
  };
}

/**
 * Blockiert diese PLZ das Speichern? Genau dann, wenn die Regel bewiesen ist
 * und nicht passt — also nur bei ausdrücklich deutschen Adressen.
 */
export function plzSperrtSpeichern(plz: string | null | undefined, land: string | null | undefined): boolean {
  const urteil = plzPruefen(plz, land);
  return urteil.haerte === "fehler" && !urteil.ok;
}

/**
 * Region für die Google-Adresssuche. Ohne bekanntes Land „de" — byte-gleich
 * zum Verhalten vor 08/2026, als die Suche hart auf Deutschland stand.
 */
export function googleRegion(land: string | null | undefined): string {
  return (isoCodeZuLand(land) ?? "DE").toLowerCase();
}
